import { Cell } from "./Cell";
import { ApplicantOrInvitee } from "../types";
import { Show } from "shared/components/helpers/Show";
import { Link } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicationStatus,
  Invitee,
  PartnerFeatures
} from "shared/types";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { getFullName } from "shared/utils/get-full-name";
import { compactNumber } from "shared/utils";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { useChatContext } from "stream-chat-react";
import { VerificationProgress } from "./VerificationProgress";
import { hasActionInManualApplication } from "views/applications-id/helpers";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { getApplicantStatus } from "utils/get-applicant-status";
import { ApplicationActionsMenu } from "shared/components/ApplicationActionsMenu";
import { ApplicationActionsMenuVariant } from "shared/components/ApplicationActionsMenu/types";
import { getApplicantRole } from "utils/getApplicantRole";

export const ApplicantHeaderCell = ({
  applicant,
  applicationId,
  pendingMagicLink,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setSetPrimaryModal,
  setRefundModal,
  setResendInviteModal,
  applicationStatus,
  allowCoSigner,
  allowOccupant,
  setManualApplicationOpen
}: {
  applicant: ApplicantOrInvitee;
  applicationId?: string;
  pendingMagicLink?: Invitee;
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setSetPrimaryModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  setResendInviteModal?: Dispatch<SetStateAction<Invitee | undefined>>;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  setManualApplicationOpen: (applicantId: number) => void;
}) => {
  const { permissions, type, partnerFeatures } = useAuthContext();
  const { adminPrivileges } = type;
  // TODO: disable chat for manual applicants
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  const { client } = useChatContext();

  const isApplicantWithChat = "verifications" in applicant && hasChat;
  const [unreads, setUnreads] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- client can be undefined at runtime outside <Chat>
    if (!isApplicantWithChat || !client) return;

    const channel = client.channel("messaging", `chat-id-${applicant.id}`);
    let subscription: ReturnType<typeof channel.on> | undefined;

    const watchChannel = async () => {
      try {
        await channel.watch();
        setUnreads(channel.countUnread());
        subscription = channel.on(() => {
          setUnreads(channel.countUnread());
        });
      } catch {
        // Channel may not exist yet
      }
    };

    watchChannel();

    return () => {
      subscription?.unsubscribe();
      channel.stopWatching().catch(() => {});
    };
  }, [isApplicantWithChat, client, applicant.id]);

  const applicantFirstName =
    "first_name" in applicant ? (applicant.first_name ?? undefined) : undefined;
  const applicantLastName =
    "last_name" in applicant ? (applicant.last_name ?? undefined) : undefined;
  const applicantMiddleName =
    "middle_name" in applicant
      ? (applicant.middle_name ?? undefined)
      : undefined;
  const inviteFirstName =
    pendingMagicLink && "first_name" in pendingMagicLink
      ? (pendingMagicLink.first_name ?? undefined)
      : undefined;
  const inviteLastName =
    pendingMagicLink && "last_name" in pendingMagicLink
      ? (pendingMagicLink.last_name ?? undefined)
      : undefined;
  const inviteMiddleName =
    pendingMagicLink && "middle_name" in pendingMagicLink
      ? (pendingMagicLink.middle_name ?? undefined)
      : undefined;

  const isApplicant = "verifications" in applicant;
  const isManual = "is_manual" in applicant && applicant.is_manual;
  const isPrimary =
    "is_primary" in applicant ? applicant.is_primary : undefined;

  const effectiveStatus = getApplicantStatus({
    applicant,
    pendingMagicLink
  });

  const chatLinkTo = isApplicant
    ? `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=chat`
    : "";
  const [finished, total] = useMemo(() => {
    // If application is finished or submitted -> simulate 100% completion
    // There are cases when count comes incorrectly from the backend,
    if (
      [ApplicantStatus.Finished, ApplicantStatus.Submitted].includes(
        applicant.application_status
      )
    )
      return [10, 10];

    if ("finished_verifications" in applicant)
      return [
        applicant.finished_verifications.finished_steps,
        applicant.finished_verifications.total_steps
      ];

    // if applicant is invitee -> simulate 0%
    return [0, 10];
  }, [applicant]);

  const getApplicantNameOrEmail = () => {
    const hasApplicantName = Boolean(
      applicantFirstName || applicantLastName || applicantMiddleName
    );
    const hasInviteName = Boolean(
      inviteFirstName || inviteLastName || inviteMiddleName
    );

    if (hasApplicantName && applicantFirstName && applicantLastName)
      return getFullName({
        firstName: applicantFirstName,
        lastName: applicantLastName,
        middleName: applicantMiddleName,
        short: true
      });

    if (hasInviteName && inviteFirstName && inviteLastName)
      return getFullName({
        firstName: inviteFirstName,
        lastName: inviteLastName || pendingMagicLink?.email || "",
        middleName: inviteMiddleName || undefined,
        short: true
      });

    return pendingMagicLink?.email || applicant.email;
  };

  return (
    <Cell className="!h-[202px] relative w-full" isDataCell>
      <div className="w-full mb-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-content-1000 truncate max-w-[240px]">
            {getApplicantNameOrEmail()}
          </h3>
          <ApplicationActionsMenu
            variant={ApplicationActionsMenuVariant.Group}
            contentClassName="z-[12]"
            triggerClassName="border-none !shadow-none"
            borderLess={false}
            applicant={applicant}
            pendingMagicLink={pendingMagicLink}
            onCancelClick={(applicantId) =>
              setCancelApplicantModal(applicantId)
            }
            onUncancelClick={(applicantId) =>
              setUncancelApplicantModal(applicantId)
            }
            onChangeRoleClick={(applicant) =>
              setChangeRoleModal(applicant as ApplicantOrInvitee)
            }
            onSetPrimaryClick={(applicant) =>
              setSetPrimaryModal(applicant as ApplicantOrInvitee)
            }
            onRefundClick={(applicantId) => setRefundModal(applicantId)}
            onResendInviteClick={(invite) => setResendInviteModal?.(invite)}
            applicationStatus={applicationStatus}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            hasAdminPrivileges={adminPrivileges}
            permissions={{
              canChangeApplicantRole: permissions.canChangeApplicantRole,
              canCancelApplications: permissions.canCancelApplications
            }}
          />
        </div>
        <div className="flex justify-between items-center text-content-400 text-sm my-1">
          <p>{getApplicantRole(applicant.role, isPrimary)}</p>
          <p className="font-light text-content-300">
            {isManual ? "Manual " : "Self-serve "}application
          </p>
        </div>

        <VerificationProgress
          className="my-6"
          status={effectiveStatus || ApplicantStatus.Pending}
          completedVerifications={finished}
          totalVerifications={total}
        />

        {isManual &&
        !(
          effectiveStatus &&
          [ApplicantStatus.Canceled, ApplicantStatus.Submitted].includes(
            effectiveStatus
          )
        ) &&
        hasActionInManualApplication({
          ...applicant,
          verifications: applicant.verifications.flat()
        }) ? (
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            className="mt-2 w-full"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setManualApplicationOpen(Number(applicant.id));
            }}
          >
            {applicant.application_started_at
              ? "Complete application"
              : "Start application"}
          </Button>
        ) : null}
        {isApplicant &&
        !(
          applicant.is_manual &&
          [
            ApplicantStatus.Pending,
            ApplicantStatus.Finished,
            ApplicantStatus.Started
          ].includes(applicant.application_status)
        ) ? (
          <div className="flex items-center gap-2">
            <Button size={ButtonSize.SM} className="w-full">
              Review
            </Button>
            <Show
              if={
                hasChat &&
                isApplicant &&
                !applicant.is_manual &&
                applicant.application_started_at
              }
            >
              <Link to={chatLinkTo} className="relative shrink-0 block">
                <IconButton
                  HugeIcon={BubbleChatIcon}
                  size={IconButtonSize.SM}
                  className="block"
                />
                {unreads ? (
                  <div className="bg-primary-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center absolute -top-1.5 -right-1 outline outline-white">
                    {compactNumber(unreads)}
                  </div>
                ) : null}
              </Link>
            </Show>
          </div>
        ) : null}
      </div>
    </Cell>
  );
};
